
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { FileUpload } from './FileUpload';
import { PlusCircle, Trash2, Link as LinkIcon, FileUp, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface ResourceItem {
  id?: string;
  title: string;
  type: string;
  url: string;
  is_required: boolean;
  isExternal?: boolean;
  fileName?: string;
}

interface ResourceManagerProps {
  resources: ResourceItem[];
  onChange: (resources: ResourceItem[]) => void;
  lessonId?: string;
  readOnly?: boolean;
}

export const ResourceManager = ({
  resources = [],
  onChange,
  lessonId,
  readOnly = false
}: ResourceManagerProps) => {
  const [resourcesList, setResourcesList] = useState<ResourceItem[]>(resources);
  const [isAddingResource, setIsAddingResource] = useState(false);
  const [newResource, setNewResource] = useState<ResourceItem>({
    title: '',
    type: 'reading',
    url: '',
    is_required: true,
    isExternal: true
  });
  const { toast } = useToast();

  useEffect(() => {
    setResourcesList(resources);
  }, [resources]);

  const handleAddResource = () => {
    if (!newResource.title.trim() || !newResource.url.trim()) {
      toast({
        title: "Missing information",
        description: "Please provide both a title and a URL for the resource",
        variant: "destructive"
      });
      return;
    }

    const updatedResources = [...resourcesList, newResource];
    setResourcesList(updatedResources);
    onChange(updatedResources);
    
    // Reset form
    setNewResource({
      title: '',
      type: 'reading',
      url: '',
      is_required: true,
      isExternal: true
    });
    setIsAddingResource(false);
    
    toast({
      title: "Resource added",
      description: `${newResource.title} has been added to the resources list`
    });
  };

  const handleRemoveResource = (index: number) => {
    const updatedResources = [...resourcesList];
    updatedResources.splice(index, 1);
    setResourcesList(updatedResources);
    onChange(updatedResources);
    
    toast({
      title: "Resource removed",
      description: "The resource has been removed from the list"
    });
  };

  const handleResourceTypeChange = (value: string) => {
    setNewResource({ ...newResource, type: value });
  };

  const handleUploadComplete = (fileUrl: string, fileName: string) => {
    // If no title provided, use the file name
    const resourceTitle = newResource.title.trim() 
      ? newResource.title 
      : fileName.substring(0, fileName.lastIndexOf('.'));
    
    setNewResource({
      ...newResource,
      title: resourceTitle,
      url: fileUrl,
      fileName,
      isExternal: false
    });
  };

  const resourceTypes = [
    { value: 'reading', label: 'Reading' },
    { value: 'video', label: 'Video' },
    { value: 'exercise', label: 'Exercise' },
    { value: 'example', label: 'Example' },
    { value: 'cheatsheet', label: 'Cheat Sheet' },
    { value: 'documentation', label: 'Documentation' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Learning Resources</h3>
        {!readOnly && (
          <Button 
            onClick={() => setIsAddingResource(!isAddingResource)}
            variant="outline"
            size="sm"
          >
            {isAddingResource ? 'Cancel' : 'Add Resource'}
            {!isAddingResource && <PlusCircle className="ml-2 h-4 w-4" />}
          </Button>
        )}
      </div>

      {isAddingResource && !readOnly && (
        <Card>
          <CardHeader>
            <CardTitle>Add Learning Resource</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resource-title">Title</Label>
              <Input
                id="resource-title"
                placeholder="Resource title"
                value={newResource.title}
                onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="resource-type">Type</Label>
              <Select
                value={newResource.type}
                onValueChange={handleResourceTypeChange}
              >
                <SelectTrigger id="resource-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {resourceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="resource-source"
                  checked={newResource.isExternal}
                  onCheckedChange={(checked) => 
                    setNewResource({ ...newResource, isExternal: checked, url: '' })
                  }
                />
                <Label htmlFor="resource-source">
                  {newResource.isExternal ? 'External URL' : 'Upload File'}
                </Label>
              </div>
              
              {newResource.isExternal ? (
                <div className="space-y-2">
                  <Label htmlFor="resource-url">URL</Label>
                  <div className="flex">
                    <div className="relative flex-grow">
                      <LinkIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="resource-url"
                        type="url"
                        placeholder="https://..."
                        className="pl-9"
                        value={newResource.url}
                        onChange={(e) => setNewResource({ ...newResource, url: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Upload File</Label>
                  <FileUpload onUploadComplete={handleUploadComplete} />
                  
                  {newResource.url && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      File: {newResource.fileName}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="resource-required"
                checked={newResource.is_required}
                onCheckedChange={(checked) => 
                  setNewResource({ ...newResource, is_required: checked })
                }
              />
              <Label htmlFor="resource-required">Mark as required</Label>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button onClick={handleAddResource}>Add Resource</Button>
          </CardFooter>
        </Card>
      )}

      {resourcesList.length === 0 ? (
        <div className="text-center p-6 border border-dashed rounded-md">
          <p className="text-muted-foreground">No resources added yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {resourcesList.map((resource, index) => (
            <div
              key={resource.id || index}
              className="flex items-center justify-between p-3 border rounded-md bg-background"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-primary/10 p-2 rounded-md">
                  {resource.isExternal ? (
                    <ExternalLink className="h-4 w-4 text-primary" />
                  ) : (
                    <FileUp className="h-4 w-4 text-primary" />
                  )}
                </div>
                <div>
                  <h4 className="font-medium">{resource.title}</h4>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <span className="capitalize">{resource.type}</span>
                    {resource.is_required && (
                      <>
                        <span>•</span>
                        <span>Required</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  asChild
                >
                  <a href={resource.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
                
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => handleRemoveResource(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
